import { Button, Container, Drawer, IconButton } from '@material-ui/core'
import { Close } from '@material-ui/icons'
import { Box } from '@mui/material'
import { useContext, useEffect, useState } from 'react'
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { getActivityByProject } from '../../actions/activityActions'
import { getProjectDetails } from '../../actions/projectActions'
import { ReactComponent as ChatIcon } from '../../assets/img/icons/comments.svg'
import {
  MACHINE_CREATE_RESET,
  MACHINE_UPDATE_RESET,
} from '../../constants/machineConstants'
import { PART_CREATE_RESET } from '../../constants/partConstants'
import {
  PROJECT_CREATE_RESET,
  PROJECT_CURRENT_STEP,
  PROJECT_EXIT_RESET,
} from '../../constants/projectConstants'
import { useInput } from '../../hooks/useInput'
import { Context } from '../../providers/ProjectContextProvider'
import ActivityPanel from '../Activity/ActivityPanel'
import AddEditMachine from '../Machine/AddEditMachine'
import AddEditPart from '../Part/AddEditPart'
import AddEditProgram from '../Program/AddEditProgram'
import ToastMessage from '../ToastMessage/ToastMessage'
import AddOnServices from './AddOnServices/AddOnServices'
import DeliveryDetails from './DeliveryDetails/DeliveryDetails'
import EstimatedCosts from './EstimatedCosts/EstimatedCosts'
import NotifyUser from './NotifyUserModal/NotifyUser'
import ProgramDetails from './ProgramDetails/ProgramDetails'
import ProjectInfo from './ProjectInfo/ProjectInfo'
import QuoteProgress from './QuoteProgress/QuoteProgress'
import ReportRequirements from './ReportRequirements/ReportRequirements'
import SelectMachine from './SelectMachine/SelectMachine'
import SelectPart from './SelectParts/SelectPart'
import SelectTimeframe from './SelectTimeframe/SelectTimeframe'
import UserDetails from './UserDetails/UserDetails'
import './projectEdit.scss'

interface CreateAQuoteProps {
  match: {
    params: {
      id: string
    }
  }
}

const CreateAQuote = ({ match }: CreateAQuoteProps) => {
  const dispatch = useDispatch()
  const history = useHistory()

  const projectId = match.params.id

  const { setShowNotifyUser } = useContext(Context)

  const [showAddPart, setShowAddPart] = useState(false)
  const [partToEdit, setPartToEdit] = useState<Part | undefined>(undefined)
  const [partTitleOverride, setPartTitleOverride] = useState('')
  const [machineToEdit, setMachineToEdit] = useState<Machine | undefined>(
    undefined
  )
  const [showAddMachine, setShowAddMachine] = useState(false)
  const [showAddProgram, setShowAddProgram] = useState(false)
  const [programToEdit, setProgramToEdit] = useState<Program | undefined>(
    undefined
  )
  const [selectedPartId, setSelectedPartId] = useState('')
  const [selectedMachineId, setSelectedMachineId] = useState('')
  const [selectedPrograms, setSelectedPrograms] = useState<Program[]>([])
  const [createPartName, setCreatePartName] = useState('')
  const [addPartToast, setAddPartToast] = useState(false)
  const [createMachineName, setCreateMachineName] = useState('')
  const [addMachineToast, setAddMachineToast] = useState(false)
  const [updateMachineToast, setUpdateMachineToast] = useState(false)
  const [updatedMachineName, setUpdatedMachineName] = useState('')
  const [showActivityPanel, setShowActivityPanel] = useState(false)

  const {
    value: hasProgram,
    bind: bindHasProgram,
    setValue: setHasProgram,
  } = useInput('')
  const {
    value: keepProgram,
    bind: bindKeepProgram,
    setValue: setKeepProgram,
  } = useInput('')
  const { value: notes, bind: bindNotes, setValue: setNotes } = useInput('')
  const [documents, setDocuments] = useState([])
  const [qty, setQty] = useState<number | undefined>(undefined)

  const { userInfo } = useSelector(
    (state: { userLogin: UserLogin }) => state.userLogin
  )
  const { project } = useSelector(
    (state: RootStateOrAny) => state.projectDetails
  )
  const { currentStep } = useSelector(
    (state: RootStateOrAny) => state.projectStep
  )
  const { exit } = useSelector((state: RootStateOrAny) => state.projectExit)
  const { createdProject, success: createSuccess } = useSelector(
    (state: RootStateOrAny) => state.projectCreate
  )
  const { createdMachine, success: createMachineSuccess } = useSelector(
    (state: RootStateOrAny) => state.machineCreate
  )
  const { createdPart, success: partCreateSuccess } = useSelector(
    (state: RootStateOrAny) => state.partCreate
  )
  const { success: machineUpdateSuccess, machine: updatedMachine } =
    useSelector((state: RootStateOrAny) => state.machineUpdate)

  const isAdmin =
    userInfo && userInfo.hasOwnProperty('isAdmin') ? userInfo.isAdmin : false

  useEffect(() => {
    if (!userInfo) {
      history.push(`/login?redirect=${window.location.pathname}`)
    }

    if (exit) {
      dispatch({
        type: PROJECT_EXIT_RESET,
      })
      history.push('/myquality/dashboard')
    }

    if (!currentStep) {
      dispatch({
        type: PROJECT_CURRENT_STEP,
        payload: 2,
      })
    }

    if (projectId) {
      if (!project || project._id !== projectId) {
        dispatch(getProjectDetails(projectId))
        dispatch(getActivityByProject(projectId))
        dispatch({ type: PROJECT_CREATE_RESET })
      }
    }

    if (createSuccess) {
      dispatch(getProjectDetails(createdProject._id))
      dispatch({ type: PROJECT_CREATE_RESET })
    }

    if (partCreateSuccess) {
      setSelectedPartId(createdPart._id)
      setCreatePartName(createdPart.name)
      setAddPartToast(true)
      dispatch({ type: PART_CREATE_RESET })
      dispatch({
        type: PROJECT_CURRENT_STEP,
        payload: 3,
      })
    }

    if (createMachineSuccess) {
      setSelectedMachineId(createdMachine._id)
      setCreateMachineName(createdMachine.name)
      setAddMachineToast(true)
      dispatch({ type: MACHINE_CREATE_RESET })

      if (project.type !== 'inspection') {
        dispatch({
          type: PROJECT_CURRENT_STEP,
          payload: 4,
        })
      }
    }

    if (machineUpdateSuccess) {
      setMachineToEdit(undefined)
      setUpdatedMachineName(updatedMachine.name)
      setUpdateMachineToast(true)
      dispatch({ type: MACHINE_UPDATE_RESET })
      window.scrollTo(0, 0)
    }
  }, [
    userInfo,
    dispatch,
    project,
    projectId,
    createSuccess,
    createdProject,
    currentStep,
    exit,
    history,
    partCreateSuccess,
    createdPart,
    createMachineSuccess,
    createdMachine,
    updatedMachine,
    machineUpdateSuccess,
  ])

  const resetRequestValues = () => {
    setSelectedMachineId('')
    setSelectedPartId('')
    setSelectedPrograms([])
    setHasProgram('')
    setKeepProgram('')
    setNotes('')
    setDocuments([])
    setQty(undefined)
  }

  const handleClose = () => {
    if (userInfo.isAdmin) {
      setShowNotifyUser(true)
    } else {
      history.push('/myquality/dashboard')
    }
  }

  return (
    <Container className="create-a-quote">
      {project && (
        <>
          <div className="flex space-between m-t-40 m-b-40">
            <h3>Create a quote request {isAdmin && <UserDetails />}</h3>

            <Box sx={{ display: 'flex' }}>
              <Button
                variant="outlined"
                className="m-r-16"
                onClick={() => setShowActivityPanel(true)}
              >
                <ChatIcon className="m-r-5" style={{ width: 20 }} /> Send
                message
              </Button>

              <IconButton className="p-l-8 p-r-8" onClick={handleClose}>
                <Close />
              </IconButton>
            </Box>
          </div>
          <QuoteProgress />
          {currentStep === 1 && <ProjectInfo />}
          {currentStep === 2 &&
            (!showAddPart ? (
              <SelectPart
                setShowAddPart={setShowAddPart}
                selectedPartId={selectedPartId}
                setSelectedPartId={setSelectedPartId}
                setPartToEdit={setPartToEdit}
                setPartTitleOverride={setPartTitleOverride}
              />
            ) : (
              <AddEditPart
                type={project.type}
                setShowAddPart={setShowAddPart}
                partToEdit={partToEdit}
                setPartToEdit={setPartToEdit}
                partTitleOverride={partTitleOverride}
              />
            ))}
          {currentStep === 3 &&
            project.type === 'programming' &&
            (!showAddMachine ? (
              <SelectMachine
                setShowAddMachine={setShowAddMachine}
                selectedMachineId={selectedMachineId}
                setSelectedMachineId={setSelectedMachineId}
                setMachineToEdit={setMachineToEdit}
              />
            ) : (
              <AddEditMachine
                setShowAddMachine={setShowAddMachine}
                machineToEdit={machineToEdit}
                setMachineToEdit={setMachineToEdit}
              />
            ))}
          {(currentStep === 3 || currentStep === '3b') &&
            project.type === 'inspection' &&
            (!showAddProgram ? (
              <ProgramDetails
                setShowAddProgram={setShowAddProgram}
                selectedPartId={selectedPartId}
                selectedPrograms={selectedPrograms}
                setSelectedPrograms={setSelectedPrograms}
                qty={qty}
                setQty={setQty}
                setProgramToEdit={setProgramToEdit}
                hasProgram={hasProgram}
                keepProgram={keepProgram}
                bindHasProgram={bindHasProgram}
                bindKeepProgram={bindKeepProgram}
                bindNotes={bindNotes}
                selectedMachineId={selectedMachineId}
                setSelectedMachineId={setSelectedMachineId}
                resetRequestValues={resetRequestValues}
              />
            ) : (
              <AddEditProgram
                setShowAddProgram={setShowAddProgram}
                programToEdit={programToEdit}
                setProgramToEdit={setProgramToEdit}
              />
            ))}
          {currentStep === 4 && (
            <ReportRequirements
              selectedPartId={selectedPartId}
              selectedMachineId={selectedMachineId}
              selectedPrograms={selectedPrograms}
              qty={qty}
              documents={documents}
              hasProgram={hasProgram}
              keepProgram={keepProgram}
              notes={notes}
              resetRequestValues={resetRequestValues}
            />
          )}
          {currentStep === 5 && <EstimatedCosts />}
          {currentStep === 6 && <SelectTimeframe />}
          {currentStep === 7 && <DeliveryDetails />}
          {currentStep === 8 && <AddOnServices />}
          {currentStep === 9 && <EstimatedCosts />}

          <NotifyUser />

          <ToastMessage
            message={
              <>
                <p className="fs16 fw600">Part added</p>
                <p className="fs14 m-b-0">
                  You successfully created {createPartName}.
                </p>
              </>
            }
            open={addPartToast}
            setOpen={setAddPartToast}
          />
          <ToastMessage
            message={
              <>
                <p className="fs16 fw600">Machine added</p>
                <p className="fs14 m-b-0">
                  You successfully created {createMachineName}.
                </p>
              </>
            }
            open={addMachineToast}
            setOpen={setAddMachineToast}
          />

          <ToastMessage
            message={
              <>
                <p className="fs16 fw600">Machine updated</p>
                <p className="fs14 m-b-0">
                  You successfully updated {updatedMachineName}.
                </p>
              </>
            }
            open={updateMachineToast}
            setOpen={setUpdateMachineToast}
          />

          <Drawer
            anchor="right"
            open={showActivityPanel}
            onClose={() => setShowActivityPanel(false)}
            className="side-panel"
          >
            <ActivityPanel />
          </Drawer>
        </>
      )}
    </Container>
  )
}

export default CreateAQuote
